import React, { useState } from "react";
import type { AdaptedUsageLog } from "@/lib/types";
import { Check, X, Clock, ChevronDown, ChevronUp, RefreshCw } from "lucide-react";
import { cn, formatUSD, formatDiscount } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";

const STATUS_MAP: Record<string, { color: string; bg: string; icon: React.ReactNode }> = {
  success: { color: "text-emerald-400", bg: "bg-emerald-500/10", icon: <Check size={10} /> },
  failed: { color: "text-red-400", bg: "bg-red-500/10", icon: <X size={10} /> },
  running: { color: "text-blue-400", bg: "bg-blue-500/10", icon: <RefreshCw size={10} className="animate-spin" /> },
  pending: { color: "text-amber-400", bg: "bg-amber-500/10", icon: <Clock size={10} /> },
};

// Funding-source pill colors, distinct from STATUS_MAP/typeColor above so the
// two badge families never read as the same signal: voucher (purple, matches
// the "data"-type tag elsewhere) / cash (emerald, "paid in full") / credit
// (amber, "drawing down a limit").
const FUNDING_MAP: Record<string, string> = {
  voucher: "text-purple-400 bg-purple-500/10 border-purple-500/20",
  cash: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20",
  credit: "text-amber-400 bg-amber-500/10 border-amber-500/20",
};

type FundingKey = "voucher" | "cash" | "credit";

// extraColumns appears between Model/Type label and the right-side Usage column,
// so admin/team views can inject e.g. team name / user email per row without
// forking the row layout.
export interface UsageLogRowProps {
  log: AdaptedUsageLog;
  extraColumn?: React.ReactNode;
  extraDetailFields?: Array<[string, string]>;
}

export function UsageLogRow({ log, extraColumn, extraDetailFields = [] }: UsageLogRowProps) {
  const { t, language } = useLanguage();
  const [expanded, setExpanded] = useState(false);
  const statusConfig = STATUS_MAP[log.status] || STATUS_MAP.failed;

  const typeColor = {
    llm: "tag-llm",
    compute: "text-cyan-400 bg-cyan-500/10 border-cyan-500/20",
    data: "text-purple-400 bg-purple-500/10 border-purple-500/20",
    scinexus: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20",
  }[log.type] || "tag-llm";

  // Billing v3: a single request can settle across up to three pools
  // (voucher/cash/credit cascade), so the old single funding_source badge is
  // replaced by a per-pool breakdown. funding_source is only populated once
  // settle has run (pending/legacy records omit it entirely — see
  // UsageRecord.funding_source json tag), so the whole section stays opt-in
  // rather than showing a row of "$0" pills for unsettled records.
  const fundingSegments: Array<[FundingKey, string]> = log.fundingSource
    ? (
        [
          ["voucher", log.voucherAmount],
          ["cash", log.cashAmount],
          ["credit", log.creditAmount],
        ] as Array<[FundingKey, string]>
      ).filter(([, amount]) => Number(amount) !== 0)
    : [];
  const fundingLabels: Record<FundingKey, string> = {
    voucher: t("logs.fundingVoucher"),
    cash: t("logs.fundingCash"),
    credit: t("logs.fundingCredit"),
  };

  return (
    <div className={cn("border-b border-white/5 last:border-0", expanded && "bg-white/2")}>
      <div
        className="flex items-center gap-4 px-5 py-3.5 hover:bg-white/3 cursor-pointer transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className={cn("w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0", statusConfig.bg, statusConfig.color)}>
          {statusConfig.icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-sm text-white font-mono truncate">{log.model}</span>
            <span className={cn("text-[9px] px-1.5 py-0.5 rounded-full border font-medium flex-shrink-0", typeColor)}>
              {log.type.toUpperCase()}
            </span>
            {fundingSegments.map(([key, amount]) => (
              <span key={key} className={cn("text-[9px] px-1.5 py-0.5 rounded-full border font-medium flex-shrink-0", FUNDING_MAP[key])}>
                {fundingLabels[key]} {formatUSD(amount)}
              </span>
            ))}
          </div>
          <div className="flex items-center gap-3 text-[10px] text-slate-600">
            <span className="flex items-center gap-1"><Clock size={9} />{log.timestamp}</span>
            {log.keyName && <span>Key: {log.keyName}</span>}
          </div>
        </div>
        {extraColumn && (
          <div className="hidden lg:block w-[160px] min-w-0 flex-shrink-0">{extraColumn}</div>
        )}
        <div className="text-right hidden sm:block w-[120px] flex-shrink-0">
          {log.tokens > 0 && <div className="text-xs font-mono text-slate-400">{log.tokens.toLocaleString()} tok</div>}
          <div className="text-[10px] text-slate-600">{log.latency > 0 ? `${(log.latency / 1000).toFixed(2)}s` : "—"}</div>
        </div>
        <div className="text-right w-[80px] flex-shrink-0">
          <div className="text-xs font-mono text-white">{`$${log.cost}`}</div>
        </div>
        <div className="w-[20px] flex-shrink-0 flex justify-center">
          {expanded ? <ChevronUp size={13} className="text-slate-600" /> : <ChevronDown size={13} className="text-slate-600" />}
        </div>
      </div>

      {expanded && (
        <div className="px-5 pb-4 ml-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            {([
              ["Request ID", log.requestId],
              ["Model", log.model],
              log.upstreamModel && ["Upstream", log.upstreamModel] as [string, string],
              log.provider && ["Provider", log.provider] as [string, string],
              ...extraDetailFields,
              ["Status", log.status],
              ["Latency", log.latency > 0 ? `${log.latency}ms` : "N/A"],
              ["Input Tokens", log.inputTokens?.toLocaleString() || "—"],
              ["Output Tokens", log.outputTokens?.toLocaleString() || "—"],
              log.cacheReadTokens > 0 && ["Cache Read Tokens", log.cacheReadTokens.toLocaleString()] as [string, string],
              log.cacheWriteTokens > 0 && ["Cache Write Tokens", log.cacheWriteTokens.toLocaleString()] as [string, string],
              // list/discount/actual 三行只在已结算(有 fundingSource)的记录上展示：
              // 历史遗留/未结算记录后端默认 listAmount=0、discountRate=1，
              // 而 cost 是真实历史扣费，直接展示会出现"折后价高于原价"的自相矛盾。
              log.fundingSource && [t("logs.listAmount"), formatUSD(log.listAmount)] as [string, string],
              log.fundingSource && [t("logs.discountRate"), formatDiscount(log.discountRate, language, t)] as [string, string],
              log.fundingSource && [t("logs.actualAmount"), `$${log.cost}`] as [string, string],
              ["API Key", log.keyName || "—"],
            ].filter(Boolean) as [string, string][]).map(([k, v]) => (
              <div key={k} className="p-2 rounded-lg bg-white/3 border border-white/8">
                <div className="text-[10px] text-slate-600 mb-0.5">{k}</div>
                <div className="text-xs text-slate-300 font-mono truncate">{v}</div>
              </div>
            ))}
          </div>
          {log.errorMessage && (
            <div className="p-2.5 rounded-lg bg-red-500/10 border border-red-500/20 text-xs text-red-400 font-mono">
              <div>Error: {log.errorMessage}</div>
              {log.errorDetail && (
                <div className="mt-1 text-[11px] text-red-300/80 break-all whitespace-pre-wrap">
                  {log.errorDetail}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
